/**
 * Language Learning (completion plan P5).
 *
 * The catalog says what can be practised (languages, regional usage,
 * self-reported CEFR levels, registers, and the authored scenarios per
 * situation with the modalities each can be graded in). A session is started
 * through the ordinary coaching API (`createCoachingSession` with
 * `languageLearning`), on the canonical conversation runtime — there is no
 * separate language session API. Progress is the learner's own, read from
 * canonical results.
 */
import { api } from '../api';

export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type LanguageRegister = 'formal' | 'informal';
export type LanguageSituation = 'travel' | 'work' | 'social';

export interface RegionalUsage {
  key: string;
  label: string;
  usage: string[];
}

export interface LanguageScenarioEntry {
  id: string;
  situation: LanguageSituation;
  title: string;
  description: string;
  difficulty: string;
  estimatedMinutes: number;
  partner: { personaKey: string; label: string };
  /** Modalities a graded attempt may be taken in (authored rubric applicability). */
  testingModalities: string[];
  /** Modalities a Development attempt may be taken in (authored or language rubric). */
  developmentModalities?: string[];
}

export interface LanguageSituationEntry {
  situation: LanguageSituation;
  label: string;
  description: string;
  status: 'available' | 'content_incomplete';
  gap?: 'no_authored_scenario' | 'partner_persona_unavailable';
  scenarios: LanguageScenarioEntry[];
}

export interface LanguageLearningCatalog {
  languages: { code: string; label: string; regions: RegionalUsage[] }[];
  proficiency: { source: 'self_reported'; levels: { level: CefrLevel; description: string }[] };
  registers: LanguageRegister[];
  situations: LanguageSituationEntry[];
  limits: { objectiveMaxChars: number };
  baseLanguage: string;
}

/** What the learner sends when starting. Their own choices; the server validates them. */
export interface LanguageLearningRequest {
  language: string;
  proficiency: CefrLevel;
  region?: string;
  register: LanguageRegister;
  situation: LanguageSituation;
  objective?: string;
}

export interface DimensionTrend {
  key: string;
  label: string;
  /** value null = UNKNOWN (not measured): never 0, never a fail. */
  points: { sessionId: string; at: string; value: number | null }[];
  measured: number;
  unknown: number;
  first: number | null;
  latest: number | null;
  change: number | null;
}

export interface LanguageProgress {
  language: string;
  label: string;
  attempts: { total: number; development: number; testing: number };
  overall: { measured: number; unknown: number; latest: number | null };
  latestSelfReportedLevel: { level: CefrLevel; source: 'self_reported' } | null;
  dimensions: DimensionTrend[];
  unavailable: { key: 'pronunciation' | 'fluency'; label: string; status: 'UNAVAILABLE'; reason: string }[];
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const languageLearningApi = api
  .enhanceEndpoints({ addTagTypes: ['LanguageLearning', 'LanguageProgress'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getLanguageLearningCatalog: builder.query<LanguageLearningCatalog, void>({
        query: () => '/coaching/language-learning',
        transformResponse: (response: Envelope<LanguageLearningCatalog>) => response.data,
        providesTags: ['LanguageLearning'],
      }),
      getLanguageProgress: builder.query<LanguageProgress[], void>({
        query: () => '/coaching/language-learning/progress',
        transformResponse: (response: Envelope<{ languages: LanguageProgress[] }>) => response.data.languages,
        providesTags: ['LanguageProgress'],
      }),
    }),
    overrideExisting: false,
  });

export const { useGetLanguageLearningCatalogQuery, useGetLanguageProgressQuery } = languageLearningApi;
